import React, { useEffect, useRef, useCallback } from 'react';

interface AccessibleModalProps {
  onClose: () => void;
  ariaLabel: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** When false, backdrop click and Escape key won't close the modal */
  dismissable?: boolean;
}

/**
 * Accessible modal wrapper that provides:
 * - role="dialog" + aria-modal="true"
 * - aria-label
 * - Focus trapping (Tab/Shift+Tab cycle within modal)
 * - Escape key closes the modal
 * - Auto-focus on first focusable element
 * - Returns focus to trigger element on close
 */
export default function AccessibleModal({ onClose, ariaLabel, children, className, style, dismissable = true }: AccessibleModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  // Save the element that had focus before the modal opened
  useEffect(() => {
    previousFocus.current = document.activeElement as HTMLElement;
  }, []);

  // Return focus on close
  useEffect(() => {
    return () => {
      previousFocus.current?.focus();
    };
  }, []);

  // Auto-focus first focusable element
  useEffect(() => {
    const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && focusable.length > 0) {
      focusable[0].focus();
    }
  }, []);

  // Escape key handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (dismissable) onClose();
      return;
    }

    // Focus trap
    if (e.key === 'Tab') {
      const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={dismissable ? onClose : undefined}>
      <div
        ref={modalRef}
        className={className || 'modal-content'}
        style={style}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </div>
  );
}
